import * as ngCore from '@angular/core'
import { isDeepEqual } from '@attain-apps/data-utils'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../../state'

const ALLOW_USERS_TO_UNLOCK_ROOMS_DIRECTLY_ATTRIBUTE =
  ents.extra.quiver.resources.data.dimensions.spaceSurveyConfigurations.entities
    .spaceSurveyConfig.attributes.allowUsersToUnlockRoomsDirectly.RESOURCE
    .payload

/**
 * Component for configuring survey settings within a Space Lite deployment.
 * Unlike the Space Full survey configuration page, only the survey tools
 * (i.e. unlock rooms) are configurable here, since functional-usage
 * configuration is not deployed for Space Lite.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.illustration.emptyState.Component,
    ds.input.checkbox.Component,
    ds.layout.page.Component,
    ds.layout.section.Component,
    ds.layout.widget.Component,
    ds.loader.Component,
  ],
  selector: 'atn-space-lite-survey-configuration',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.space.surveyConfiguration.Feature} */
  public readonly surveyConfigurationFeature = ngCore.inject(
    state.space.surveyConfiguration.Feature,
  )
  /** @see {@link state.me.Feature} */
  public readonly meFeature = ngCore.inject(state.me.Feature)

  /**
   * Computed signal for the page title, which uses the name of the survey
   * configuration if available, or falls back to a default title if not.
   */
  public readonly titleSg = ngCore.computed(
    (config = this.surveyConfigurationFeature.selectors.config.asSignal()) =>
      config?.name ?? 'Survey Configuration',
  )

  /**
   * Label for the unlock-rooms tools checkbox.
   */
  public readonly allowUsersToUnlockRoomsDirectlyLabel =
    ALLOW_USERS_TO_UNLOCK_ROOMS_DIRECTLY_ATTRIBUTE.name

  /**
   * Determines whether the save button should be enabled based on whether there
   * are unsaved changes, whether the config is still loading, or if there is an
   * error state that needs to be resolved.
   */
  public readonly canSaveSg = ngCore.computed(
    (
      config = this.surveyConfigurationFeature.selectors.config.asSignal(),
      editedConfig = this.surveyConfigurationFeature.selectors.editedConfig.asSignal(),
      error = this.surveyConfigurationFeature.selectors.error.asSignal(),
      isLoading = this.surveyConfigurationFeature.selectors.isLoading.asSignal(),
    ) => {
      if (isLoading || error || !editedConfig) {
        return false
      }

      return !isDeepEqual(config, editedConfig)
    },
  )

  /**
   * Updates the currently edited survey-tools configuration values as they
   * are changed in the form
   */
  public toolsChangedHandler(
    updates: Partial<ents.extra.space.SurveyConfiguration.Tools>,
  ): void {
    this.surveyConfigurationFeature.actions.updateTools
      .from('Space.Lite.SurveyConfiguration.Tools.Changed')
      .dispatch(updates)
  }

  /**
   * Dispatches the upsert action to persist the currently edited survey
   * configuration
   */
  public saveClickHandler(): void {
    this.surveyConfigurationFeature.actions.upsert
      .from('Space.Lite.SurveyConfiguration.Save.Clicked')
      .dispatch()
  }
}
